import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Stack,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SIGNALS, inputSx, selectSx, TV } from '../../constants';

const SequenceWithExclusionDetails = ({ sub, idx, onUpdate, bulkStrategyTimeframe }) => {
  const [signalInput, setSignalInput] = useState('');
  const [excludedSignalInput, setExcludedSignalInput] = useState('');

  const signals = sub.condition?.signals || [];
  const excludedSignals = sub.condition?.excluded_signals || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography sx={{ fontSize: '11px', color: TV.accent.primary, fontWeight: 600, mb: 1.5, textTransform: 'uppercase' }}>
          Signals (In Order)
        </Typography>
      </Grid>

      {signals.map((sig, sigIdx) => (
        <Grid item xs={12} key={sigIdx}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: TV.accent.warning, minWidth: '60px' }}>
              Signal {sigIdx + 1}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Signal</InputLabel>
              <Select 
                value={typeof sig === 'string' ? sig : sig.name || ''} 
                label="Signal"
                onChange={(e) => {
                  const updated = [...signals];
                  updated[sigIdx] = e.target.value;
                  onUpdate(idx, 'condition', { ...sub.condition, signals: updated });
                }}
                sx={selectSx}
              >
                <MenuItem value="">-- Select --</MenuItem>
                {SIGNALS.map(signal => <MenuItem key={signal} value={signal}>{signal}</MenuItem>)}
              </Select>
            </FormControl>
            <Button 
              size="small" 
              onClick={() => {
                const updated = signals.filter((_, i) => i !== sigIdx);
                onUpdate(idx, 'condition', { ...sub.condition, signals: updated });
              }}
              sx={{ color: TV.accent.danger, minWidth: '32px', p: 1 }}
            >
              <DeleteIcon sx={{ fontSize: '16px' }} />
            </Button>
          </Stack>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl fullWidth size="small">
            <InputLabel>Signal</InputLabel>
            <Select 
              value={signalInput} 
              label="Signal"
              onChange={(e) => setSignalInput(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">-- Select --</MenuItem>
              {SIGNALS.map(signal => <MenuItem key={signal} value={signal}>{signal}</MenuItem>)}
            </Select>
          </FormControl>
          <Button 
            size="small" 
            onClick={() => {
              if (signalInput.trim()) {
                onUpdate(idx, 'condition', { 
                  ...sub.condition, 
                  signals: [...signals, signalInput] 
                });
                setSignalInput('');
              }
            }}
            sx={{ color: TV.accent.primary, fontWeight: 600 }}
          >
            <AddIcon sx={{ fontSize: '16px' }} />
          </Button>
        </Stack>
      </Grid>

      <Grid item xs={12}><Box sx={{ height: '1px', bgcolor: TV.border.primary }} /></Grid>

      <Grid item xs={12}>
        <Typography sx={{ fontSize: '11px', color: TV.accent.primary, fontWeight: 600, mb: 1.5, textTransform: 'uppercase' }}>
          Excluded Signals
        </Typography>
      </Grid>

      {excludedSignals.map((sig, sigIdx) => (
        <Grid item xs={12} key={sigIdx}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl fullWidth size="small">
              <InputLabel>Signal</InputLabel>
              <Select 
                value={typeof sig === 'string' ? sig : sig.name || ''} 
                label="Signal"
                onChange={(e) => {
                  const updated = [...excludedSignals];
                  updated[sigIdx] = e.target.value;
                  onUpdate(idx, 'condition', { ...sub.condition, excluded_signals: updated });
                }}
                sx={selectSx}
              >
                <MenuItem value="">-- Select --</MenuItem>
                {SIGNALS.map(signal => <MenuItem key={signal} value={signal}>{signal}</MenuItem>)}
              </Select>
            </FormControl>
            <Button 
              size="small" 
              onClick={() => {
                const updated = excludedSignals.filter((_, i) => i !== sigIdx);
                onUpdate(idx, 'condition', { ...sub.condition, excluded_signals: updated });
              }}
              sx={{ color: TV.accent.danger, minWidth: '32px', p: 1 }}
            >
              <DeleteIcon sx={{ fontSize: '16px' }} />
            </Button>
          </Stack>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl fullWidth size="small">
            <InputLabel>Signal</InputLabel>
            <Select 
              value={excludedSignalInput} 
              label="Signal"
              onChange={(e) => setExcludedSignalInput(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">-- Select --</MenuItem>
              {SIGNALS.map(signal => <MenuItem key={signal} value={signal}>{signal}</MenuItem>)}
            </Select>
          </FormControl>
          <Button 
            size="small" 
            onClick={() => {
              if (excludedSignalInput.trim()) {
                onUpdate(idx, 'condition', { 
                  ...sub.condition, 
                  excluded_signals: [...excludedSignals, excludedSignalInput] 
                });
                setExcludedSignalInput('');
              }
            }}
            sx={{ color: TV.accent.primary, fontWeight: 600 }}
          >
            <AddIcon sx={{ fontSize: '16px' }} />
          </Button>
        </Stack>
      </Grid>

      <Grid item xs={12}><Box sx={{ height: '1px', bgcolor: TV.border.primary }} /></Grid>

      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth size="small" type="number" 
          label="Min Minutes" 
          value={sub.condition?.min_minutes ?? ''} 
          onChange={(e) => {
            const value = e.target.value === '' ? '' : parseFloat(e.target.value);
            onUpdate(idx, 'condition', { ...sub.condition, min_minutes: value });
          }} 
          sx={inputSx} 
          inputProps={{ min: 0, step: '0.1' }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth size="small" type="number" 
          label="Max Minutes" 
          value={sub.condition?.max_minutes ?? ''} 
          onChange={(e) => {
            const value = e.target.value === '' ? '' : parseFloat(e.target.value);
            onUpdate(idx, 'condition', { ...sub.condition, max_minutes: value });
          }} 
          sx={inputSx} 
          inputProps={{ min: 0, step: '0.1' }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={sub.condition?.reset_on_excluded ?? false}
              onChange={(e) => onUpdate(idx, 'condition', { ...sub.condition, reset_on_excluded: e.target.checked })}
              sx={{ color: TV.accent.primary, '&.Mui-checked': { color: TV.accent.primary } }}
            />
          }
          label={<Typography sx={{ fontSize: '12px', color: TV.text.primary }}>Reset on Excluded Signal</Typography>}
        />
      </Grid>
    </Grid>
  );
};

export default SequenceWithExclusionDetails;
